import React, {
  useState,
  useEffect,
} from "react";

import {
  ShieldCheck,
  CalendarDays,
  UploadCloud,
  LoaderCircle,
  X,
  FileText,
} from "lucide-react";

import {
  toast,
} from "react-toastify";

import "../styles/RenewalModal.css";

const API_BASE_URL =
  "https://client-management-backend-8x91.onrender.com";

const RenewalModal = ({
  isOpen,
  onClose,
  onSave,
  clientId,
}) => {

  const [
    renewalDate,
    setRenewalDate,
  ] = useState("");

  const [
    nextRenewalDate,
    setNextRenewalDate,
  ] = useState("");

  const [
    policyDocument,
    setPolicyDocument,
  ] = useState(null);

  const [
    uploading,
    setUploading,
  ] = useState(false);

  /* =====================================
      ESC CLOSE
  ===================================== */

  useEffect(() => {

    if (!isOpen)
      return;

    const handleKeyDown =
      (e) => {

        if (
          e.key === "Escape" &&
          !uploading
        ) {

          onClose();

        }

      };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );

    };

  }, [
    isOpen,
    uploading,
    onClose,
  ]);

  /* =====================================
      FILE UPLOAD
  ===================================== */

  const handleFileUpload =
    (e) => {

      setPolicyDocument(
        e.target.files[0]
      );

    };

  /* =====================================
      SUBMIT
  ===================================== */

  const handleSubmit =
    async () => {

      if (
        !renewalDate ||
        !nextRenewalDate ||
        !policyDocument
      ) {

        toast.warn(
          "Complete all fields."
        );

        return;

      }

      const formData =
        new FormData();

      formData.append(
        "file",
        policyDocument
      );

      formData.append(
        "client_id",
        clientId
      );

      formData.append(
        "renewal_date",
        renewalDate
      );

      formData.append(
        "next_renewal_date",
        nextRenewalDate
      );

      try {

        setUploading(true);

        const response =
          await fetch(
            `${API_BASE_URL}/renewals`,
            {

              method: "POST",

              body: formData,

            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.policy_document
        ) {

          throw new Error(
            data.error ||
            "Upload failed"
          );

        }

        toast.success(
          "Renewal added successfully!"
        );

        onSave(data);

        onClose();

      } catch (error) {

        console.error(error);

        toast.error(
          "Renewal upload failed."
        );

      } finally {

        setUploading(false);

      }

    };

  /* =====================================
      HIDE
  ===================================== */

  if (!isOpen)
    return null;

  /* =====================================
      RETURN
  ===================================== */

  return (

    <div className="renewal-modern-overlay">

      <div className="renewal-modern-modal">

        {/* CLOSE */}

        <button
          className="renewal-modern-close"
          onClick={onClose}
          disabled={uploading}
        >

          <X size={18} />

        </button>

        {/* LEFT */}

        <div className="renewal-modern-left">

          <div className="renewal-modern-badge">

            <ShieldCheck size={14} />

            POLICY RENEWAL

          </div>

          <h1>

            Insurance
            Renewal

          </h1>

          <p>

            Upload updated policy
            information and track
            future renewal dates.
          </p>

          <div className="renewal-modern-preview-card">

            <div className="renewal-modern-preview-icon">

              <FileText size={22} />

            </div>

            <div>

              <h4>
                Client Locked
              </h4>

              <span>

                Client ID:
                {" "}
                {clientId}

              </span>

            </div>

          </div>

        </div>

        {/* RIGHT */}

        <div className="renewal-modern-right">

          <div className="renewal-modern-header">

            <h2>

              Add Renewal

            </h2>

            <p>

              Complete renewal
              information below.

            </p>

          </div>

          {/* DATES */}

          <div className="renewal-modern-grid">

            {/* DATE */}

            <div className="renewal-modern-field">

              <label>

                <CalendarDays
                  size={14}
                />

                Renewal Date

              </label>

              <input
                type="date"
                value={
                  renewalDate
                }
                onChange={(e) =>
                  setRenewalDate(
                    e.target.value
                  )
                }
              />

            </div>

            {/* NEXT DATE */}

            <div className="renewal-modern-field">

              <label>

                <CalendarDays
                  size={14}
                />

                Next Renewal

              </label>

              <input
                type="date"
                value={
                  nextRenewalDate
                }
                onChange={(e) =>
                  setNextRenewalDate(
                    e.target.value
                  )
                }
              />

            </div>

          </div>

          {/* FILE */}

          <div className="renewal-modern-upload-card">

            <div className="renewal-modern-upload-header">

              <UploadCloud
                size={22}
              />

              <div>

                <h4>

                  Policy Document

                </h4>

                <p>

                  Upload PDF or
                  image policy file.

                </p>

              </div>

            </div>

            <label className="renewal-modern-upload-btn">

              {
                policyDocument
                  ? "Replace Document"
                  : "Upload Document"
              }

              <input
                type="file"
                hidden
                accept="application/pdf,image/*"
                onChange={
                  handleFileUpload
                }
              />

            </label>

            {policyDocument && (

              <div className="renewal-modern-selected-file">

                <FileText
                  size={18}
                />

                <span>

                  {
                    policyDocument.name
                  }

                </span>

              </div>

            )}

          </div>

          {/* ACTIONS */}

          <div className="renewal-modern-actions">

            <button
              className="renewal-modern-save-btn"
              onClick={
                handleSubmit
              }
              disabled={
                uploading
              }
            >

              {uploading ? (

                <>

                  <LoaderCircle
                    size={18}
                    className="spin"
                  />

                  Uploading...

                </>

              ) : (

                <>

                  <ShieldCheck
                    size={18}
                  />

                  Save Renewal

                </>

              )}

            </button>

            <button
              className="renewal-modern-cancel-btn"
              onClick={onClose}
              disabled={
                uploading
              }
            >

              Cancel

            </button>

          </div>

        </div>

      </div>

    </div>

  );

};

export default RenewalModal;